import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, STATUS_OPTIONS, TIPOS_PREDEFINIDOS, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function ServicoFormDialog({
    open,
    onClose,
    onSaved,
    clientes = [],
    motos = [],
    initial = null,
    defaultDate = "",
}) {
    const [form, setForm] = useState({
        cliente_id: "",
        moto_id: "",
        tipo: "Revisão",
        tipoCustom: "",
        descricao: "",
        data: defaultDate || "",
        hora: "09:00",
        status: "pendente",
    });
    const [saving, setSaving] = useState(false);
    const [useCustom, setUseCustom] = useState(false);

    useEffect(() => {
        if (open) {
            if (initial) {
                const isPreset = TIPOS_PREDEFINIDOS.includes(initial.tipo);
                setForm({
                    cliente_id: initial.cliente_id,
                    moto_id: initial.moto_id,
                    tipo: isPreset ? initial.tipo : "Outro",
                    tipoCustom: isPreset ? "" : initial.tipo,
                    descricao: initial.descricao || "",
                    data: initial.data,
                    hora: initial.hora || "09:00",
                    status: initial.status,
                });
                setUseCustom(!isPreset);
            } else {
                setForm({
                    cliente_id: "",
                    moto_id: "",
                    tipo: "Revisão",
                    tipoCustom: "",
                    descricao: "",
                    data: defaultDate || new Date().toISOString().slice(0, 10),
                    hora: "09:00",
                    status: "pendente",
                });
                setUseCustom(false);
            }
        }
    }, [open, initial, defaultDate]);

    const motosCliente = motos.filter((m) => !form.cliente_id || m.cliente_id === form.cliente_id);

    async function handleSave() {
        if (!form.cliente_id || !form.moto_id || !form.data) {
            toast.error("Preencha cliente, moto e data.");
            return;
        }
        const tipoFinal = useCustom ? form.tipoCustom.trim() : form.tipo;
        if (!tipoFinal) {
            toast.error("Indique o tipo de trabalho.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                cliente_id: form.cliente_id,
                moto_id: form.moto_id,
                tipo: tipoFinal,
                descricao: form.descricao,
                data: form.data,
                hora: form.hora,
                status: form.status,
            };
            if (initial) {
                await api.put(`/servicos/${initial.id}`, payload);
                toast.success("Serviço atualizado.");
            } else {
                await api.post("/servicos", payload);
                toast.success("Serviço criado.");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg rounded-sm" data-testid="servico-dialog">
                <DialogHeader>
                    <DialogTitle className="font-['Outfit']">
                        {initial ? "Editar marcação" : "Nova marcação"}
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Cliente
                        </Label>
                        <Select
                            value={form.cliente_id}
                            onValueChange={(v) => setForm({ ...form, cliente_id: v, moto_id: "" })}
                        >
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="servico-cliente-select">
                                <SelectValue placeholder="Selecionar cliente" />
                            </SelectTrigger>
                            <SelectContent>
                                {clientes.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.nome}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Moto
                        </Label>
                        <Select
                            value={form.moto_id}
                            onValueChange={(v) => setForm({ ...form, moto_id: v })}
                            disabled={!form.cliente_id}
                        >
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="servico-moto-select">
                                <SelectValue placeholder={form.cliente_id ? "Selecionar moto" : "Selecione primeiro um cliente"} />
                            </SelectTrigger>
                            <SelectContent>
                                {motosCliente.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>
                                        {m.marca} {m.modelo} {m.num_chassis ? `· ${m.num_chassis}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Data
                        </Label>
                        <Input
                            type="date"
                            value={form.data}
                            onChange={(e) => setForm({ ...form, data: e.target.value })}
                            className="mt-1.5 h-10 rounded-sm"
                            data-testid="servico-data-input"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Hora
                        </Label>
                        <Input
                            type="time"
                            value={form.hora}
                            onChange={(e) => setForm({ ...form, hora: e.target.value })}
                            className="mt-1.5 h-10 rounded-sm"
                            data-testid="servico-hora-input"
                        />
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Tipo de trabalho
                        </Label>
                        {!useCustom ? (
                            <Select
                                value={form.tipo}
                                onValueChange={(v) => {
                                    if (v === "__custom__") {
                                        setUseCustom(true);
                                    } else {
                                        setForm({ ...form, tipo: v });
                                    }
                                }}
                            >
                                <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="servico-tipo-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TIPOS_PREDEFINIDOS.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                    <SelectItem value="__custom__">Outro (escrever)…</SelectItem>
                                </SelectContent>
                            </Select>
                        ) : (
                            <div className="mt-1.5 flex gap-2">
                                <Input
                                    value={form.tipoCustom}
                                    onChange={(e) => setForm({ ...form, tipoCustom: e.target.value })}
                                    placeholder="Descrição do trabalho"
                                    className="h-10 rounded-sm"
                                    data-testid="servico-tipo-custom-input"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="rounded-sm"
                                    onClick={() => setUseCustom(false)}
                                >
                                    Predefinidos
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Estado
                        </Label>
                        <Select
                            value={form.status}
                            onValueChange={(v) => setForm({ ...form, status: v })}
                        >
                            <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="servico-status-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">
                            Descrição / Notas
                        </Label>
                        <Textarea
                            value={form.descricao}
                            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                            placeholder="Detalhes do trabalho, peças, observações..."
                            className="mt-1.5 rounded-sm"
                            rows={3}
                            data-testid="servico-descricao-input"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="servico-cancel-btn">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="servico-save-btn"
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                    >
                        {saving ? "A guardar..." : "Guardar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
